/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {useDispatch, useSelector, shallowEqual} from 'react-redux'
import {LeadModel} from './models/LeadModel'
import {AgentModel} from '../agents/models/AgentModel'
import {LeadStatusModel} from '../leadStatus/models/LeadStatusModel'
import {actions} from './redux/LeadsRedux'
import MultilineEdit from './MultilineEdit'
import {RootState} from '../../../setup'

type Props = {
  lead: LeadModel
  columns: string[]
  index: string
}

const LeadRow: FC<Props> = ({lead, columns, index}) => {
  const dispatch = useDispatch()
  const [isUpdating, setIsUpdating] = useState(false)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const [isNotesUpdating, setIsNotesUpdating] = useState(false)

  // Get agents from the store
  const agents = useSelector<RootState>(({agents}) => agents.agents, shallowEqual) as AgentModel[]

  // Get lead statuses from the store
  const leadStatuses = useSelector<RootState>(
    ({leads}) => leads.leadStatuses,
    shallowEqual
  ) as LeadStatusModel[]

  // Function to format the timestamp to a readable date
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return 'N/A'

    const date = new Date(timestamp * 1000)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  const updateNote = (value: string) => {
    if (value !== lead.notes) {
      setIsNotesUpdating(true)
      dispatch(actions.updateLead(lead.id, {notes: value}))

      // Reset updating state after a short delay
      setTimeout(() => {
        setIsNotesUpdating(false)
      }, 1000)
    }
  }

  const assignToAgent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const agentId = e.target.value
    setIsUpdating(true)

    // Dispatch update action
    dispatch(actions.updateLead(lead.id, {agentId}))

    // Reset updating state after a short delay to ensure the Redux store is updated
    setTimeout(() => {
      setIsUpdating(false)
    }, 1000)
  }

  const assignLeadStatus = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const leadStatusId = e.target.value
    setIsStatusUpdating(true)

    // Dispatch update action
    dispatch(actions.updateLead(lead.id, {leadStatusId}))

    // Reset updating state after a short delay to ensure the Redux store is updated
    setTimeout(() => {
      setIsStatusUpdating(false)
    }, 1000)
  }

  const getCellValue = (
    columnHeader: string,
    columnIndex: number,
    lastIndex: boolean,
    form: any
  ): JSX.Element => {
    return <td key={columnIndex}>{form[columnHeader]}</td>
  }

  const getRow = () => {
    let columnElements: JSX.Element[] = []
    if (columns && columns.length > 0) {
      columns.forEach((column: string, colIndex: number) => {
        columnElements.push(
          getCellValue(column, colIndex, colIndex === columns.length - 1, lead.form)
        )
      })
    }

    // Add created_on date column
    columnElements.push(
      <td key='created-on' className=''>
        <span className='text-muted fw-bold d-block fs-7'>{formatDate(lead.created_on)}</span>
      </td>
    )

    columnElements.push(
      <td key='notes-column' className='notes-cell'>
        <div className='d-flex align-items-center'>
          {isNotesUpdating && (
            <div className='me-2'>
              <span className='spinner-border spinner-border-sm text-primary' />
            </div>
          )}
          <div className='flex-grow-1'>
            <MultilineEdit
              value={lead.notes}
              setValue={updateNote}
              placeholder='Add notes for this lead...'
            />
          </div>
        </div>
      </td>
    )

    // Add lead status dropdown
    columnElements.push(
      <td key='status-dropdown' className='text-center'>
        <select
          className={`form-select form-select-sm ${
            isStatusUpdating ? 'bg-light-primary' : 'form-select-solid'
          }`}
          value={lead.leadStatusId || ''}
          onChange={assignLeadStatus}
          disabled={isStatusUpdating}
        >
          <option value=''>Select Status</option>
          {leadStatuses &&
            leadStatuses.map((status: LeadStatusModel) => (
              <option key={status.id} value={status.id}>
                {status.status}
              </option>
            ))}
        </select>
        {isStatusUpdating && (
          <span className='ms-2'>
            <span className='spinner-border spinner-border-sm align-middle'></span>
          </span>
        )}
      </td>
    )

    // Add agent assignment dropdown
    columnElements.push(
      <td key='agent-dropdown' className='text-end'>
        <select
          className={`form-select form-select-sm ${
            isUpdating ? 'bg-light-primary' : 'form-select-solid'
          }`}
          value={lead.agentId || ''}
          onChange={assignToAgent}
          disabled={isUpdating}
        >
          <option value=''>Assign to Agent</option>
          {agents &&
            agents.map((agent: AgentModel) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
        </select>
        {isUpdating && (
          <span className='ms-2'>
            <span className='spinner-border spinner-border-sm align-middle'></span>
          </span>
        )}
      </td>
    )

    return <tr key={index}>{columnElements}</tr>
  }
  return getRow()
}

export default LeadRow
